// What the MQL5 VM could not run, from real user uploads.
//
// This is the triage surface for the import VM: every upload it cannot handle is recorded, and the
// same defect from a hundred users collapses to ONE row with a count. The list is ordered by how
// many people each issue blocks, because that is the order worth fixing in.
//
// The "Copy digest" button is the point of the whole screen — it produces the markdown to hand
// straight to a developer, so nothing has to be retyped or summarised by hand.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const KIND_LABEL = {
  unresolved: 'Missing from the VM',
  runtime: 'Failed while running',
  parse: 'Could not be parsed',
  include: 'Unresolved #include',
  journal: 'Journal error',
};

const KIND_HINT = {
  unresolved: 'A name with no shim. Usually the quickest kind to fix.',
  runtime: 'The EA started, then hit something unimplemented mid-run.',
  parse: 'The front end could not read the source at all.',
  include: 'An #include we neither ship nor stub.',
};

function timeAgo(value) {
  if (!value) return '';
  const then = new Date(String(value).replace(' ', 'T') + (String(value).endsWith('Z') ? '' : 'Z'));
  const seconds = Math.max(0, Math.floor((Date.now() - then.getTime()) / 1000));
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

export default function VmDiagnosticsPanel() {
  const [issues, setIssues] = useState([]);
  const [totals, setTotals] = useState({ distinct: 0, occurrences: 0, open: 0, unseen: 0 });
  const [status, setStatus] = useState('open');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');
  const [logFile, setLogFile] = useState('');

  const api = process.env.REACT_APP_API_URL;
  const authHeader = useCallback(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` },
  }), []);

  // ack=1 clears the unread marker. Only the explicit visit does that — a background poll for the
  // badge must not silently mark things as read.
  const load = useCallback(async (acknowledge) => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(
        `${api}/api/admin/vm-diagnostics?status=${encodeURIComponent(status)}${acknowledge ? '&ack=1' : ''}`,
        authHeader(),
      );
      setIssues(response.data.issues || []);
      setTotals(response.data.totals || {});
      setLogFile(response.data.logFile || '');
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Could not load VM diagnostics.');
    } finally {
      setLoading(false);
    }
  }, [api, status, authHeader]);

  useEffect(() => { load(true); }, [load]);

  const setIssueStatus = async (signature, next) => {
    try {
      await axios.post(`${api}/api/admin/vm-diagnostics/${signature}/status`, { status: next }, authHeader());
      await load(false);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Could not update that issue.');
    }
  };

  const copyDigest = async () => {
    try {
      const response = await axios.get(
        `${api}/api/admin/vm-diagnostics/digest?status=${encodeURIComponent(status)}`,
        { ...authHeader(), responseType: 'text' },
      );
      const text = typeof response.data === 'string' ? response.data : String(response.data);
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        setCopied('Digest copied — paste it into the dev chat.');
      } else {
        // Clipboard access needs a secure context; opening the text is the honest fallback rather
        // than claiming a copy that did not happen.
        const win = window.open('', '_blank');
        if (win) { win.document.write(`<pre>${text.replace(/</g, '&lt;')}</pre>`); win.document.close(); }
        setCopied('Clipboard unavailable here — the digest opened in a new tab.');
      }
      setTimeout(() => setCopied(''), 6000);
    } catch (requestError) {
      setError('Could not build the digest.');
    }
  };

  return (
    <div className="section-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0 }}>VM Errors</h2>
          <p style={{ margin: '6px 0 0', opacity: 0.75, maxWidth: 620 }}>
            What the MQL5 import VM could not run, collected from real uploads. Identical failures
            are grouped, so the count is how many times it happened and the user count is how many
            people it blocked. Source code is never stored — only the API surface that failed.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="open">Open</option>
            <option value="fixed">Fixed</option>
            <option value="wontfix">Won&apos;t fix</option>
            <option value="all">All</option>
          </select>
          <button type="button" onClick={() => load(true)} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          <button type="button" onClick={copyDigest} disabled={!issues.length}>Copy digest</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 24, margin: '16px 0', flexWrap: 'wrap' }}>
        <div><strong style={{ fontSize: 22 }}>{totals.open ?? 0}</strong><div style={{ opacity: 0.7 }}>open issues</div></div>
        <div><strong style={{ fontSize: 22 }}>{totals.occurrences ?? 0}</strong><div style={{ opacity: 0.7 }}>occurrences shown</div></div>
        <div><strong style={{ fontSize: 22 }}>{totals.unseen ?? 0}</strong><div style={{ opacity: 0.7 }}>new since last visit</div></div>
      </div>

      {error && <div style={{ color: '#c0392b', marginBottom: 12 }}>{error}</div>}
      {copied && <div style={{ color: '#1e8449', marginBottom: 12 }}>{copied}</div>}

      {!loading && !issues.length && (
        <p style={{ opacity: 0.7 }}>
          Nothing here — the VM ran everything it was given.
        </p>
      )}

      {issues.map((issue) => (
        <div
          key={issue.signature}
          style={{
            border: '1px solid rgba(128,128,128,0.3)',
            borderLeft: `4px solid ${issue.isNew ? '#e67e22' : 'rgba(128,128,128,0.3)'}`,
            borderRadius: 6,
            padding: '10px 14px',
            marginBottom: 10,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, opacity: 0.75, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  {KIND_LABEL[issue.kind] || issue.kind}
                </span>
                {issue.isNew && <span style={{ fontSize: 11, color: '#e67e22', fontWeight: 700 }}>NEW</span>}
              </div>
              <div style={{ fontWeight: 600, marginTop: 4, wordBreak: 'break-word' }}>
                {issue.symbol || issue.message}
              </div>
              {issue.symbol && issue.message && !issue.message.startsWith(issue.symbol) && (
                <div style={{ opacity: 0.8, fontFamily: 'monospace', fontSize: 12, marginTop: 4, wordBreak: 'break-word' }}>
                  {issue.message}
                </div>
              )}
              <div style={{ opacity: 0.7, fontSize: 12, marginTop: 6 }}>
                {issue.occurrences}x
                {issue.userCount > 0 && ` · ${issue.userCount} user${issue.userCount === 1 ? '' : 's'}`}
                {issue.eaName && ` · first in ${issue.eaName}`}
                {` · last ${timeAgo(issue.lastSeen)}`}
              </div>
              {KIND_HINT[issue.kind] && (
                <div style={{ opacity: 0.55, fontSize: 12, marginTop: 2 }}>{KIND_HINT[issue.kind]}</div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
              {issue.status !== 'fixed' && (
                <button type="button" onClick={() => setIssueStatus(issue.signature, 'fixed')}>Fixed</button>
              )}
              {issue.status !== 'wontfix' && (
                <button type="button" onClick={() => setIssueStatus(issue.signature, 'wontfix')}>Won&apos;t fix</button>
              )}
              {issue.status !== 'open' && (
                <button type="button" onClick={() => setIssueStatus(issue.signature, 'open')}>Reopen</button>
              )}
            </div>
          </div>
        </div>
      ))}

      {logFile && (
        <p style={{ opacity: 0.6, fontSize: 12, marginTop: 16 }}>
          Every individual occurrence is also appended to <code>{logFile}</code>.
          Marking something fixed hides it, but it reopens by itself if it happens again.
        </p>
      )}
    </div>
  );
}
